import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Converts an HTML element to a PDF and downloads it.
 * The PDF is scaled to fit on a single page while maintaining aspect ratio.
 *
 * @param element       - The HTML element to convert to PDF.
 * @param filename      - The filename for the downloaded PDF (should include .pdf extension).
 * @param loggedInUser  - The name of the user who is downloading the PDF.
 */
export const downloadElementAsPdf = async (element: HTMLElement, filename: string, loggedInUser: string): Promise<void> => {
    // Convert the component to a canvas.
    const canvas = await html2canvas(element, {
        // Ensure consistent output width regardless of viewport size. Why 1320? I don't know. It just seems to be the value that fits a 1 page PDF perfectly.
        windowWidth: 1320,
        ignoreElements: (element) => {
            // Remove buttons in the page header.
            if (element.tagName === 'BUTTON') {
                let parent = element.parentElement;
                while (parent) {
                    if (Array.from(parent.classList).some((cls) => cls.includes('pageHeaderContent'))) {
                        return true;
                    }
                    parent = parent.parentElement;
                }
            }

            // Remove links styled as buttons in the page header.
            if (element.tagName === 'DIV' && Array.from(element.classList).some((cls) => cls.includes('primaryAsLink') || cls.includes('secondaryAsLink'))) {
                let parent = element.parentElement;
                while (parent) {
                    if (Array.from(parent.classList).some((cls) => cls.includes('pageHeaderContent'))) {
                        return true;
                    }
                    parent = parent.parentElement;
                }
            }

            // Remove the breadcrumb navigation in the page header (MUI Breadcrumbs renders as a nav with a CSS class name containing the string "MuiBreadcrumbs").
            if (element.tagName === 'NAV' && Array.from(element.classList).some((cls) => cls.includes('MuiBreadcrumbs'))) {
                return true;
            }

            return false;
        },
    });

    // Convert the canvas to a PNG.
    const data = canvas.toDataURL('image/png');

    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgProperties = pdf.getImageProperties(data);
    const aspectRatio = imgProperties.width / imgProperties.height;

    // Scale to fill page width, maintaining aspect ratio.
    let finalWidth = pageWidth;
    let finalHeight = pageWidth / aspectRatio;

    // If height exceeds page, shrink to fit.
    if (finalHeight > pageHeight) {
        finalHeight = pageHeight;
        finalWidth = pageHeight * aspectRatio;
    }

    // Write the PNG to a PDF.
    pdf.addImage(data, 'PNG', 0, 0, finalWidth, finalHeight);

    // Add footer to show who downloaded the PDF and at what time.
    const timestamp = new Date().toLocaleString(undefined, { timeZoneName: 'short' });
    const footerText = `Downloaded at ${timestamp} by ${loggedInUser}`;
    pdf.setFontSize(10);
    pdf.setTextColor(128, 128, 128); // Gray color
    pdf.text(footerText, 5, pageHeight - 5);

    // Trigger the download.
    pdf.save(filename);
};
